import PropTypes from 'prop-types';
import { Box, Button, Skeleton, Typography } from '@mui/material';
import AlertSubscriptionItem from 'features/IRWorkstation/components/AlertCenter/AlertSubscriptionItem';

const AlertRowSkeleton = () => (
	<Box
		sx={{
			display: 'flex',
			alignItems: 'flex-start',
			gap: '9px',
			padding: '9px 15px',
			borderBottom: '0.5px solid',
			borderColor: 'border.light',
		}}
	>
		<Skeleton
			variant='circular'
			width={7}
			height={7}
			sx={{ mt: '4px', flexShrink: 0 }}
		/>
		<Box sx={{ flex: 1 }}>
			<Skeleton width={'20%'} height={20} />
			<Skeleton width={'40%'} height={18} />

			<Box
				sx={{
					display: 'flex',
					gap: '6px',
					mt: '2px',
				}}
			>
				<Skeleton width={50} height={14} />
				<Skeleton width={40} height={14} />
				<Skeleton width={60} height={14} />
				<Skeleton width={40} height={14} />
			</Box>
		</Box>

		<Skeleton
			variant='rectangular'
			width={36}
			height={18}
			sx={{ borderRadius: 2 }}
		/>
	</Box>
);

const AlertListSkeleton = () => (
	<Box data-testid='alert-list-skeleton'>
		{Array.from({ length: 8 }).map((_, index) => (
			<AlertRowSkeleton key={index} />
		))}
	</Box>
);

const EmptyState = ({ message }) => (
	<Box
		display='flex'
		justifyContent='center'
		alignItems='center'
		sx={{ height: '100%', width: '100%' }}
	>
		<Typography color='text.secondary'>{message}</Typography>
	</Box>
);

EmptyState.propTypes = {
	message: PropTypes.string.isRequired,
};

const AlertSubscriptionList = ({
	subscribedAlerts,
	allAlerts,
	loading,
	subscribe,
	unsubscribe,
	filter,
	setFilter,
	error,
}) => {
	const visibleAlerts = filter === 'subscribed' ? subscribedAlerts : allAlerts;
	const emptyMessage =
		error || (filter === 'subscribed' ? 'No subscribed alerts' : 'No alerts');

	return (
		<Box
			sx={{
				display: 'flex',
				flexDirection: 'column',
				height: '100%',
			}}
		>
			{/* Filter buttons and sort */}
			<Box
				sx={{
					display: 'flex',
					justifyContent: 'space-between',
					alignItems: 'center',
					mb: 4,
					flexShrink: 0,
				}}
			>
				<Box sx={{ display: 'flex', gap: 1 }}>
					{['Subscribed', 'All'].map((filterOption) => (
						<Button
							key={filterOption}
							variant={
								filter === filterOption.toLowerCase() ? 'contained' : 'outlined'
							}
							size='small'
							onClick={() => setFilter(filterOption.toLowerCase())}
							disabled={loading || !!error}
						>
							{filterOption}
						</Button>
					))}
				</Box>
			</Box>

			<Box
				sx={{
					flex: 1,
					minHeight: 0,
					overflowY: 'auto',
					border: '0.5px solid',
					borderColor: 'border.light',
					borderRadius: '8px',
				}}
			>
				{loading ? (
					<AlertListSkeleton />
				) : visibleAlerts.length === 0 || error ? (
					<EmptyState message={emptyMessage} />
				) : (
					visibleAlerts.map((alert) => (
						<AlertSubscriptionItem
							key={alert.id}
							alert={alert}
							handleUnsubscribe={unsubscribe}
							handleSubscribe={subscribe}
						/>
					))
				)}
			</Box>
		</Box>
	);
};

AlertSubscriptionList.propTypes = {
	allAlerts: PropTypes.array.isRequired,
	subscribedAlerts: PropTypes.array.isRequired,
	loading: PropTypes.bool.isRequired,
	subscribe: PropTypes.func.isRequired,
	unsubscribe: PropTypes.func.isRequired,
	filter: PropTypes.string.isRequired,
	setFilter: PropTypes.func.isRequired,
	error: PropTypes.string,
};

export default AlertSubscriptionList;
